import React, {Component} from "react"
import Papa from "papaparse"

import {Label} from "@irati/ui/src/Typography"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import adminReportingService from "@irati/adminReporting/services/adminReporting"
import authentication from "@irati/auth"
import Flex from "@irati/ui/src/Flex"
import Button from "@irati/ui/src/Button"
import makeCSV from "@irati/utils/csv/makeCSV"
import {Tooltip} from "@irati/ui/src/Tooltip"
import {DealCategory, DealStatus, PerkRedemptionsReport} from "@irati/types"
import dealsService from "@irati/deals/services/dealsGraph"
import devToggles from "@irati/dev-toggles"

import teamsService from "@irati/identity/services/teams"
import getTotalSavings from "../columns/getTotalSavings"
import {ALL_TEAMS, LAST_30_DAYS, LAST_12_MONTHS, MONTHLY, Option, Filters, QUARTERLY} from "../constants"
import {getDealMainCategory} from "../utils"

interface Props extends InjectIntl {
  showButtons: boolean
  timeRangeOptions: Option[]
  timeRange?: Date | string
  filters: Filters
  regionOptions: Option[]
  dealCategories: DealCategory[]
  onCallback: () => void
}

interface State {
  load: Promise<void> | null
}

class MostPopularPerks extends Component<Props, State> {
  state = {
    load: null
  }

  buildTeams = () => {
    const {filters} = this.props
    if (filters.businessUnitIds?.length) {
      return filters.businessUnitIds
    }
    if (filters.teamId !== ALL_TEAMS) {
      return [filters.teamId]
    }
  }

  getTimeRangeDuration = () => {
    const {timeRange, filters} = this.props
    if (filters?.month?.quarter) {
      return QUARTERLY
    }
    if (timeRange === LAST_30_DAYS || timeRange === LAST_12_MONTHS) {
      return timeRange
    }
    return MONTHLY
  }

  downloadReport = () => {
    const {
      formatMessage,
      timeRange,
      timeRangeOptions,
      filters: {businessUnitIds, teamId, month, currency},
      regionOptions,
      dealCategories,
      onCallback
    } = this.props

    onCallback()
    const businessId = authentication.business().uuid
    const load = adminReportingService
      .getBusinessRedemptionReport(
        {
          businessId,
          limit: 0,
          duration: this.getTimeRangeDuration(),
          month: timeRange === LAST_30_DAYS || timeRange === LAST_12_MONTHS ? undefined : month,
          teams: this.buildTeams()
        },
        {fetchPolicy: "network-only"}
      )
      .then(async (res) => {
        const topPerks = res.report.perks
        let time = timeRangeOptions.find((date) => date.value === timeRange)?.label

        if (timeRange !== LAST_12_MONTHS && timeRange !== LAST_30_DAYS) {
          const nestedOptions = [...timeRangeOptions[0].value, ...timeRangeOptions[1].value]
          time = nestedOptions.find((date) => date.value === timeRange)?.label
        }
        const countries = businessUnitIds?.length
          ? regionOptions
              .filter((option) => businessUnitIds.find((value) => value === option.value))
              .map((option) => option.label.replace("platform.billingUnit.", ""))
              .join(", ")
          : formatMessage({id: "admin.components.searchAndSort.allCountries"})
        const team = teamId !== ALL_TEAMS ? await teamsService.get({tenantId: businessId, teamId}) : teamId

        let headers = [
          formatMessage({id: "admin.reporting.popularPerks.csv.headers.internalIdentifier"}),
          formatMessage({id: "admin.reporting.popularPerks.csv.headers.time"}),
          formatMessage({id: "admin.reporting.popularPerks.csv.headers.country"}),
          formatMessage({id: "admin.reporting.popularPerks.csv.headers.team"}),
          formatMessage({id: "admin.reporting.popularPerks.csv.headers.category"}),
          formatMessage({id: "admin.reporting.popularPerks.csv.headers.name"}),
          formatMessage({id: "admin.reporting.popularPerks.csv.headers.redemptions"}),
          formatMessage({id: "admin.reporting.popularPerks.csv.headers.users"}),
          formatMessage({id: "admin.reporting.popularPerks.csv.headers.type"}),
          formatMessage({id: "admin.reporting.popularPerks.csv.headers.currency"}),
          formatMessage({id: "admin.reporting.popularPerks.csv.headers.savings"}),
          formatMessage({id: "admin.reporting.popularPerks.csv.headers.savingsType"})
        ]
        if (!devToggles.showTopRedeemedPerksCategory()) {
          headers = headers.filter((h) => h !== formatMessage({id: "admin.reporting.popularPerks.csv.headers.category"}))
        }
        return await dealsService
          .list(
            {
              uuids: topPerks.map((item: PerkRedemptionsReport) => item.dealId),
              limit: topPerks.length,
              ignoreBlacklist: true,
              ignorePackage: true,
              statuses: [DealStatus.On, DealStatus.Off, DealStatus.Unavailable, DealStatus.Hidden, DealStatus.Archived]
            },
            {fetchPolicy: "network-only"}
          )
          .then(({results}) => {
            const data = topPerks.map((perk: PerkRedemptionsReport) => {
              const perkUsesEstimatedSavings = perk.type === "% discount voucher" || perk.type === "discount voucher"
              const mainCategory = getDealMainCategory(results.find((p) => p.uuid === perk.dealId)?.tagIds, dealCategories)
              const row = [
                perk.internalIdentifier,
                time,
                countries,
                team?.teamName || team,
                mainCategory,
                perk.header,
                perk.redemptions,
                perk.uniqueUsers,
                perk.type,
                perk.type !== "% discount voucher" && perk.type !== "discount voucher" && perk?.savingsPercentage > 0 ? "%" : currency,
                getTotalSavings(perk, currency),
                perkUsesEstimatedSavings ? formatMessage({id: "admin.reporting.popularPerks.csv.data.savingsType"}) : ""
              ]
              return !devToggles.showTopRedeemedPerksCategory() ? row.filter((_, index) => index !== 4) : row
            })

            const csv = Papa.unparse({fields: headers, data})
            makeCSV(csv, "topRedeemedPerks.csv")
          })
      })
    this.setState({load})
  }

  render() {
    const {load} = this.state
    const {showButtons, formatMessage} = this.props

    return (
      <Flex direction="column">
        <Label>
          <FormattedMessage id="admin.reporting.popularPerks.discountInfo" />
        </Label>
        <Flex justifyContent="space-between" marginTop={1}>
          <Label>
            *<FormattedMessage id="admin.reporting.popularPerks.dataInfo" />
          </Label>
          {showButtons && (
            <Tooltip tooltipChildren={formatMessage({id: "admin.reporting.button.tooltip"})} placement="left">
              <Button primary onClick={this.downloadReport} id="adminSection_reporting_downloadTopRedeemedPerks_button" load={load}>
                <FormattedMessage id="admin.reporting.button.downloadReport" />
              </Button>
            </Tooltip>
          )}
        </Flex>
      </Flex>
    )
  }
}

export default injectIntl(MostPopularPerks)
